/**
 * @fileoverview Syndicator Showdown — Browser-Compatible Hash Utilities
 * @module @ss/shared/hashUtils
 *
 * Browser equivalent of the canonical serialisation and SHA-256 logic
 * used server-side in functions/src/integrity/generateHashManifest.js.
 *
 * Uses the Web Crypto API (available in all modern browsers and Node 18+)
 * instead of Node's `crypto` module, making these functions safe to import
 * in the React frontend.
 *
 * ─── WHY THIS EXISTS ──────────────────────────────────────────────────────────
 *
 * The ReviewSlip shows the player a "Selection Fingerprint" — a SHA-256 hash
 * of their current allocations before submission. This gives them an auditable
 * record they can compare against the post-lock manifest to verify nothing
 * changed between their submission and the race jump.
 *
 * The fingerprint does NOT include `lockedAt` (unknown until the race jumps).
 * The server adds `lockedAt` when generating the official manifest hash.
 * A mismatch between pre-submission fingerprint and post-lock hash is expected
 * (the timestamp was added) — what matters is that the RUNNER SELECTIONS match.
 *
 * ─── VERIFICATION FLOW FOR PLAYERS ──────────────────────────────────────────
 *
 * 1. Player submits stable — ReviewSlip shows their selection fingerprint
 * 2. Race jumps — server generates official hash manifest
 * 3. Player fetches their race_allocation document from their stable history
 * 4. Player removes the `lockedAt` field, re-hashes using these functions
 * 5. Resulting hash should match their selectionHash in hash_manifests
 *
 * ─── ADD TO shared/package.json exports ──────────────────────────────────────
 *
 * "./hashUtils": "./src/hashUtils.js"
 */

'use strict';

import { HASH_ALGORITHM } from './constants.js';

// Web Crypto's subtle.digest() requires 'SHA-256' (uppercase, hyphenated),
// while Node's crypto.createHash() requires 'sha256' (lowercase, no hyphen)
// — same algorithm, two different name formats. Deriving one from the other
// keeps HASH_ALGORITHM in shared/src/constants.js the single source of truth,
// so generateHashManifest.js (server) and this file (browser) can never drift.
const WEB_CRYPTO_ALGORITHM = HASH_ALGORITHM.replace(/^sha-?(\d+)$/i, 'SHA-$1');

// ─── Canonical Serialisation ──────────────────────────────────────────────────

/**
 * Deterministically serialises any value to a JSON string with sorted keys.
 * Identical to the server-side version in generateHashManifest.js.
 * Must produce the same output for the same input on both client and server.
 *
 * @param {*} value
 * @returns {string}
 */
export function canonicalSerialize(value) {
  if (value === null || value === undefined) return JSON.stringify(value);
  if (typeof value !== 'object') return JSON.stringify(value);

  // Handle Date objects
  if (value instanceof Date) return JSON.stringify(value.toISOString());

  // Handle Firestore Timestamp-like objects (toDate method)
  if (typeof value.toDate === 'function') {
    return JSON.stringify(value.toDate().toISOString());
  }

  if (Array.isArray(value)) {
    return '[' + value.map(canonicalSerialize).join(',') + ']';
  }

  const sortedKeys = Object.keys(value).sort();
  const pairs = sortedKeys.map(
    (k) => JSON.stringify(k) + ':' + canonicalSerialize(value[k])
  );
  return '{' + pairs.join(',') + '}';
}

// ─── Browser SHA-256 ──────────────────────────────────────────────────────────

/**
 * Computes SHA-256 hash of any value via canonical serialisation.
 * Uses the Web Crypto API (async) — available in all modern browsers and Node 18+.
 *
 * @param {*} data - Any value; will be canonically serialised before hashing
 * @returns {Promise<string>} 64-character lowercase hex string
 */
export async function sha256(data) {
  const serialised  = canonicalSerialize(data);
  const msgBuffer   = new TextEncoder().encode(serialised);
  const hashBuffer  = await crypto.subtle.digest(WEB_CRYPTO_ALGORITHM, msgBuffer);
  const hashArray   = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ─── Selection Fingerprint Builder ────────────────────────────────────────────

/**
 * Builds the hash input object for a player's race allocation.
 * Mirrors buildHashInput() in generateHashManifest.js but excludes lockedAt
 * (which is only known server-side at lock time).
 *
 * @param {string} userId
 * @param {string} slateId
 * @param {string} raceId
 * @param {Object} allocationData - The race_allocation object from EntryContext
 * @returns {Object} Canonical hash input (without lockedAt)
 */
export function buildSelectionFingerprintInput(userId, slateId, raceId, allocationData) {
  const runnerEntries = Object.entries(allocationData.runners || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([runnerId, runner]) => ({
      runnerId,
      equityPercent:        runner.equityPercent,
      totalCostPaid:        runner.purchaseCost,
      isFallbackAssignment: false,
    }));

  return {
    userId,
    slateId,
    raceId,
    // lockedAt deliberately omitted — unknown until race jumps
    totalSpentOnRace: allocationData.totalSpentOnRace || 0,
    runnerCount:      allocationData.runnersPicked    || 0,
    runners:          runnerEntries,
  };
}

/**
 * Computes the pre-submission selection fingerprint for one race allocation.
 *
 * @param {string} userId
 * @param {string} slateId
 * @param {string} raceId
 * @param {Object} allocationData
 * @returns {Promise<string>} SHA-256 hex fingerprint
 */
export async function computeSelectionFingerprint(userId, slateId, raceId, allocationData) {
  const input = buildSelectionFingerprintInput(userId, slateId, raceId, allocationData);
  return sha256(input);
}